import { shallowRef, ref } from 'vue'
import FlexSearch from 'flexsearch'
import type { Todo, Anniversary, TravelPlan, DiaryEntry } from '@api/generated'
import { todoApi } from '@api/services/todo'
import { anniversaryApi } from '@api/services/anniversary'
import { travelApi } from '@api/services/travel'
import { diaryApi } from '@api/services/diary'

export type SearchResultType = 'todo' | 'anniversary' | 'travel' | 'diary'

export interface SearchResult {
  id: string
  type: SearchResultType
  title: string
  subtitle: string
  route: string
}

const TYPE_LABELS: Record<SearchResultType, string> = {
  todo: '待办',
  anniversary: '纪念日',
  travel: '旅行',
  diary: '日记'
}

const index = new FlexSearch.Index({
  tokenize: 'forward',
  preset: 'score'
})

const itemCache = new Map<string, SearchResult>()
let loaded = false
const isIndexing = ref(false)

async function buildIndex() {
  if (loaded || isIndexing.value) return
  isIndexing.value = true

  const allResults: SearchResult[] = []

  try {
    const [todoRes, annRes, travelRes, diaryRes] = await Promise.allSettled([
      todoApi.listTodos({ limit: 200 }),
      anniversaryApi.listAnniversaries({ limit: 200 }),
      travelApi.listTravelPlans(),
      diaryApi.listDiary({ limit: 200 })
    ])

    if (todoRes.status === 'fulfilled') {
      for (const t of todoRes.value.data.items as Todo[]) {
        allResults.push({
          id: `todo-${t.id}`,
          type: 'todo',
          title: t.title,
          subtitle: `${TYPE_LABELS.todo} · ${t.status}`,
          route: '/'
        })
      }
    }

    if (annRes.status === 'fulfilled') {
      for (const a of annRes.value.data.items as Anniversary[]) {
        allResults.push({
          id: `ann-${a.id}`,
          type: 'anniversary',
          title: a.name,
          subtitle: `${TYPE_LABELS.anniversary} · ${a.date}`,
          route: '/anniversaries'
        })
      }
    }

    if (travelRes.status === 'fulfilled') {
      for (const t of travelRes.value.data as TravelPlan[]) {
        allResults.push({
          id: `travel-${t.id}`,
          type: 'travel',
          title: t.title,
          subtitle: `${TYPE_LABELS.travel}${t.subtitle ? ' · ' + t.subtitle : ''}`,
          route: `/travel/${t.id}`
        })
      }
    }

    if (diaryRes.status === 'fulfilled') {
      for (const d of diaryRes.value.data.items as DiaryEntry[]) {
        const preview = d.content.slice(0, 60).replace(/\n/g, ' ')
        allResults.push({
          id: `diary-${d.id}`,
          type: 'diary',
          title: d.createdAt.slice(0, 10),
          subtitle: `${TYPE_LABELS.diary} · ${preview}`,
          route: '/diary'
        })
      }
    }

    for (const r of allResults) {
      index.add(r.id, `${r.title} ${r.subtitle}`)
      itemCache.set(r.id, r)
    }
    loaded = true
  } finally {
    isIndexing.value = false
  }
}

export function useSearchIndex() {
  const results = shallowRef<SearchResult[]>([])
  const query = ref('')

  function search(q: string) {
    query.value = q
    if (!q.trim()) {
      results.value = []
      return
    }
    if (!loaded) return
    const ids = index.search(q, { limit: 20 }) as unknown as string[]
    results.value = ids.map((id) => itemCache.get(id)!).filter(Boolean)
  }

  return { results, query, search, buildIndex, isIndexing }
}
